import { useState, useEffect } from 'react';
import { useI18n } from '../../lib/i18n';
import Modal from '../ui/Modal';
import { Button } from '../ui/Button';
import { saveKnowledgeSet, type KnowledgeSetDoc } from '../../lib/aiAgents';

interface KnowledgeSetModalProps {
  open: boolean;
  set?: KnowledgeSetDoc | null;
  onClose: () => void;
  onSaved: () => void;
}

export function KnowledgeSetModal({ open, set, onClose, onSaved }: KnowledgeSetModalProps) {
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (set) {
      setName(set.name);
      setDescription(set.description || '');
    } else {
      setName('');
      setDescription('');
    }
    setError(null);
  }, [set, open]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError(t('ai.knowledge.nameRequired'));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await saveKnowledgeSet({
        id: set?.id,
        name: name.trim(),
        description: description.trim() || undefined,
      });
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save knowledge set');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={set ? t('ai.knowledge.edit') : t('ai.knowledge.create')}
    >
      <form onSubmit={handleSave} className="space-y-4">
        {error && (
          <div className="rounded-lg bg-danger-50 p-3 text-xs text-danger dark:bg-rose-950/40 dark:text-rose-400">
            {error}
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
            {t('ai.knowledge.name')} *
          </label>
          <input
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('ai.kset.namePlaceholder') || 'e.g. Products & Pricing FAQ'}
            className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
            {t('ai.knowledge.description')}
          </label>
          <textarea
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Documentation, return policies, or store catalog data"
            className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
          />
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-line dark:border-slate-700">
          <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" loading={saving}>
            {t('common.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
